import { getDB } from "./storageService";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { secureFetch } from "./batchSecurity";
import { queueManager } from "./queueManager";
import { ApplyForSignedURL } from "./batchDelivery";

const calculateBatchSize = (files) => {
    const totalBytes = files.reduce((acc, f) => acc + f.size, 0);
    return parseFloat((totalBytes / (1024 * 1024)).toFixed(4));
};

/**
 * 1. INITIALIZE & REGISTER (Handshake + Staging)
 */
export const registerNewBatch = async (files, config, taxonomyMetadata) => {
    try {
        const userGuid = sessionStorage.getItem("SEC_USER_GUID");
        const sessionId = sessionStorage.getItem("SEC_SESSION_ID");
        const companyId = sessionStorage.getItem("SEC_USER_COMPANY_ID");

        const payload = {
            BatchType: taxonomyMetadata.batchType || 1,
            SiteUserGuid: userGuid,
            SessionID: sessionId,
            CompanyID: companyId,
            BatchStatus: 10,
            FileCount: files.length,
            TotalBatchSizeMB: calculateBatchSize(files),
            ManifestFileName: taxonomyMetadata.manifestFileName || ""
        };

        const rawInitData = await secureFetch("/api/v1/batch/initialize-batch", "POST", config.siteKey, "Initialize", payload);
        const initRes = GoldenSourceHelper.normalize(rawInitData);
        if (!initRes.success) throw new Error(initRes.message || "Batch initialization failed.");

        const assignedBatchId = initRes.getProp("assignedBatchID");

        const fileListJson = files.map(f => ({
            name: f.name,
            path: taxonomyMetadata.targetRoot || "/uploads/",
            s3Key: `${taxonomyMetadata.targetRoot || ""}${f.name}`,
            sizeMB: parseFloat((f.size / (1024 * 1024)).toFixed(4))
        }));

        await secureFetch("/api/v1/batch/register-batch-files", "POST", config.siteKey, "Register", {
            BatchId: assignedBatchId,
            SiteUserGuid: userGuid,
            FileListJson: JSON.stringify(fileListJson)
        });

        const newBatch = {
            batchId: assignedBatchId,
            hash: initRes.getProp("hash"),         
            timestamp: initRes.getProp("timestamp"), 
            status: "Registered",
            siteKey: config.siteKey,
            files: files.map((f) => ({ name: f.name, size: f.size, status: "Staged", progress: 0 })),
            ...taxonomyMetadata 
        };

        const db = getDB(config.siteKey);
        await db.batches.add(newBatch);
        return newBatch;
    } catch (err) {
        console.error("Batch Registration Error:", err);
        throw err;
    }
};

/**
 * 2. DELIVER FILES
 * Orchestrates JIT Authorization and Corrects the Telemetry Hand-off.
 */
export const deliverFiles = async (batchId, files, siteKey, onProgress) => {
    try {
        const fileNames = files.map(f => f.name);
        
        // 🛡️ VITAL: Your JIT Authorization remains intact.
        const instructions = await ApplyForSignedURL(batchId, fileNames, siteKey);
        
        // ⚡️ STRATEGIC FIX: 
        // We pass only the 4 arguments the manager expects. 
        // We attach 'instructions' to the batch metadata in the manager if needed, 
        // but we do NOT let it displace the 'onProgress' callback.
        queueManager.enqueue(batchId, files, siteKey, onProgress);

        return { success: true };
    } catch (err) {
        console.error("Delivery Pipeline Failed:", err);
        throw err;
    }
};

export const refreshBatch = async (batchId, siteKey) => {
    try {
        const rawData = await secureFetch(`/api/v1/batch/refresh-batch/${batchId}`, "GET", siteKey, "Refresh");
        const res = GoldenSourceHelper.normalize(rawData);
        const db = getDB(siteKey);
        await db.batches.where("batchId").equals(batchId).modify({
            hash: res.getProp("hash"),
            timestamp: res.getProp("timestamp"),
        });
        return res.data;
    } catch (err) {
        console.error("Batch refresh failed:", err);
        throw err;
    }
};

export const deleteBatch = async (batchId, siteKey) => {
    try {
        const db = getDB(siteKey);
        await db.batches.where("batchId").equals(batchId).delete();
        await db.files.where("batchId").equals(batchId).delete();
        queueManager.cancelBatch(batchId);
    } catch (err) {
        console.error("Delete failed:", err);
        throw err;
    }
};